import { useEffect, useState, useCallback } from "react";
import { io } from "socket.io-client";
import { useLoginUser } from "../useLoginUser";
import {
  ATS_SOCKET_API_URL,
  WORKFORCE_SOCKET_API_URL
} from "../../redux/apiConfig";
import registerReceivers from "./registerReceivers";
import { useDispatch } from "react-redux";

const WF_SOCKET_URL = WORKFORCE_SOCKET_API_URL || "http://localhost:8080";
const ATS_SOCKET_URL = ATS_SOCKET_API_URL || "http://localhost:9090";

// ✅ SINGLE INSTANCE PER APP
let socketInstance = {
  workforce: null,
  ats: null,
};

const getSocketURL = (socketFor) => {
  if (socketFor === "ats") return ATS_SOCKET_URL;
  return WF_SOCKET_URL;
};

// ✅ CREATE SOCKET ONLY ONCE
const createSocket = (organizationId, userId, socketFor, dispatch) => {
  if (!organizationId || !userId) return null;

  if (socketInstance[socketFor]) {
    return socketInstance[socketFor]; // 🔥 IMPORTANT
  }

  const ENV = import.meta.env.VITE_APP_ENV || "local";
  const path =
    ENV === "local"
      ? `/${ENV}/socket.io`
      : `/${socketFor}/${ENV}/socket.io`;

  const socketURL = getSocketURL(socketFor);

  const socket = io(socketURL, {
    path,
    withCredentials: true,
    query: {
      organizationId: String(organizationId),
      userId: String(userId),
    },
    reconnection: true,
    reconnectionAttempts: 5,
    timeout: 10000,
  });

  socket.on("connect", () => {
    console.log(
      `🔌 Socket connected [${socketFor}] →`,
      socket.id
    );
  });

  socket.on("disconnect", (reason) => {
    console.log(`❌ Socket disconnected (${reason})`);
  });

  socket.on("connect_error", (err) => {
    console.error("⚠️ Socket error:", err.message);
  });

  // ✅ REGISTER RECEIVERS ONCE
  registerReceivers(socket, dispatch);

  socketInstance[socketFor] = socket;
  return socket;
};

// ❌ DO NOT AUTO DISCONNECT
export const disconnectSocket = (socketFor) => {
  if (socketInstance[socketFor]) {
    socketInstance[socketFor].disconnect();
    socketInstance[socketFor] = null;
  }
};

export const useSocket = (socketFor = "workforce") => {
  const { loginUserUiid, userData } = useLoginUser();
  const dispatch = useDispatch();
  const [socket, setSocket] = useState(null);

  const userId = loginUserUiid;
  const organizationId = userData?.organizationId?._id;

  useEffect(() => {
    if (!userId || !organizationId) return;

    const s = createSocket(
      organizationId,
      userId,
      socketFor,
      dispatch
    );

    setSocket(s);

    // ❌ DO NOTHING ON CLEANUP
    return () => {};
  }, [userId, organizationId, socketFor, dispatch]);

  const emitEvent = useCallback(
    (eventName, data) => {
      if (socket?.connected) {
        socket.emit(eventName, data);
      }
    },
    [socket]
  );

  const listenEvent = useCallback(
    (eventName, callback) => {
      if (!socket) return;
      socket.on(eventName, callback);
      return () => socket.off(eventName, callback);
    },
    [socket]
  );

  return {
    socket,
    emitEvent,
    listenEvent,
  };
};
